/* istanbul ignore file */
import { all, fork, takeEvery, put, select } from 'redux-saga/effects';
import {
  ISearchClient,
  RankedSearchModel,
  RankedSearchDslConfigurationNameResponse,
  GetRankedSearchResponse,
  GetCitizenFromDfdgResponse,
  GetCitizenByCprResponse,
  GetCompanyByProductionUnitIdentifierResponse,
  GetCompanyFromCvrByProductionUnitIdentifierResponse,
  RankedSearchFilterField,
} from '../../../../api-service/clients/auto-generated-clients';
import { moduleClientFactory } from '../../../../api-service/module-client-factory';
import { isValidCpr, formatCprNoDash, isStringOnlyNumbers } from '../../../../utilities';
import { deconstructError, isAuthError } from '../../../../utilities/error-helper';
import { NotifyErrorAction } from './notify-reducer';
import { isValidProductionUnitIdentifier } from '../../../../utilities/production-unit-identifier-helper';
import { ISelectedFilter } from '../../components/search-drawer/filters/search-drawer-custom-filters';

export type ShowDrawer = { type: 'rankedSearch/showDrawer'; showDrawer: boolean };
export type UpdateQuery = { type: 'rankedSearch/updateQuery'; query: string };
export type ExecuteQuery = {
  type: 'rankedSearch/executeQuery';
  result: RankedSearchModel[];
  totalHits: number;
  hasMoreResults: boolean;
  page: number;
  filterFields: RankedSearchFilterField[];
};
export type ExecuteQuerySaga = { type: 'rankedSearch/executeQuerySaga'; resetSearchResult: boolean; createCitizenEnabled: boolean };
export type ExecuteGetDocumentTypesSaga = { type: 'rankedSearch/executeGetDocumentTypesSaga'; universe: string };
export type UpdateUniverse = { type: 'rankedSearch/updateUniverse'; universe: string };
export type UpdateHasError = { type: 'rankedSearch/updateHasError'; hasError: boolean };
export type UpdateIsLoading = { type: 'rankedSearch/updateIsloading'; isLoading: boolean };
export type UpdateHaving = { type: 'rankedSearch/updateHaving'; havings: IHaving[] };
export type ResetSearchResult = { type: 'rankedSearch/resetSearchResult' };
export type GetCitizenFromCim = { type: 'rankedSearch/getCitizenFromCim'; result: RankedSearchModel[]; isAuthorized: boolean };
export type GetCitizenFromDfdgSaga = { type: 'rankedSearch/getCitizenFromDfdgSaga'; cpr: string };

export type GetCompanyFromService = { type: 'rankedSearch/getCompanyFromService'; result: RankedSearchModel[]; query: string };
export type GetCompanyFromCvr = { type: 'rankedSearch/getCompanyFromCvr'; productionUnitIdentifier: string };
export type CustomFiltersChangeSaga = { type: 'rankedSearch/customFiltersChangeSaga'; selectedFilters: ISelectedFilter[] };
export type CustomFiltersChange = { type: 'rankedSearch/customFiltersChange'; selectedFilters: ISelectedFilter[] };

export type ResetAll = { type: 'rankedSearch/resetAll' };

export type UseSimpleSearchAction = { type: 'rankedSearch/useSimpleSearch'; useSimpleSearch: boolean };

export type RankedSearchAction =
  | ShowDrawer
  | UpdateQuery
  | UpdateUniverse
  | ExecuteQuery
  | UpdateIsLoading
  | UpdateHasError
  | UpdateHaving
  | ResetSearchResult
  | GetCitizenFromCim
  | GetCompanyFromService
  | CustomFiltersChange
  | ResetAll
  | UseSimpleSearchAction;

export interface IHaving {
  label: string;
  value: string;
  isChecked: boolean;
  universe: string;
}

export interface IRankedSearchState {
  showDrawer: boolean;
  query: string;
  isLoading: boolean;
  isCprQuery: boolean;
  isUnauthorizedCprQuery: boolean;
  isCvrQuery: boolean;
  universe: string;
  rankedSearchResult?: RankedSearchModel[];
  totalHits: number;
  hasError: boolean;
  noResults?: boolean;
  havings: IHaving[];
  hasMoreResults: boolean;
  page: number;
  filterFields: RankedSearchFilterField[];
  selectedFilters: ISelectedFilter[];
}

export const initState: IRankedSearchState = {
  showDrawer: false,
  query: '',
  isLoading: false,
  isCprQuery: false,
  isUnauthorizedCprQuery: false,
  isCvrQuery: false,
  universe: 'citizen',
  havings: [],
  totalHits: 0,
  hasMoreResults: false,
  page: 0,
  hasError: false,
  filterFields: [],
  selectedFilters: [] as ISelectedFilter[],
};

export function rankedSearchReducer(state: IRankedSearchState = initState, action: RankedSearchAction = {} as RankedSearchAction): IRankedSearchState {
  switch (action.type) {
    case 'rankedSearch/showDrawer':
      return { ...state, showDrawer: action.showDrawer };
    case 'rankedSearch/updateQuery':
      return { ...state, query: action.query };
    case 'rankedSearch/updateUniverse':
      return { ...state, universe: action.universe };
    case 'rankedSearch/executeQuery':
      return {
        ...state,
        rankedSearchResult: action.result,
        totalHits: action.totalHits,
        noResults: action.result.length === 0,
        hasError: false,
        hasMoreResults: action.hasMoreResults,
        page: action.page,
        isCprQuery: false,
        isUnauthorizedCprQuery: false,
        filterFields: action.filterFields,
      };
    case 'rankedSearch/updateIsloading':
      return { ...state, isLoading: action.isLoading };
    case 'rankedSearch/updateHasError':
      return { ...state, hasError: action.hasError };
    case 'rankedSearch/updateHaving':
      return { ...state, havings: action.havings };
    case 'rankedSearch/resetSearchResult':
      return { ...state, rankedSearchResult: [], page: 0, hasError: false, totalHits: 0, hasMoreResults: false, isCprQuery: false, isUnauthorizedCprQuery: false };
    case 'rankedSearch/getCitizenFromCim':
      return {
        ...state,
        rankedSearchResult: action.result,
        page: 0,
        hasError: false,
        totalHits: action.result.length,
        noResults: action.result.length === 0,
        hasMoreResults: false,
        isCprQuery: action.result.length === 0,
        isUnauthorizedCprQuery: !action.isAuthorized,
      };
    case 'rankedSearch/getCompanyFromService':
      return {
        ...state,
        rankedSearchResult: action.result,
        page: 0,
        hasError: false,
        totalHits: action.result.length,
        noResults: action.result.length === 0,
        hasMoreResults: false,
        isCvrQuery: isValidProductionUnitIdentifier(state.query) && action.result.length === 0, // This will show cvr query button
      };
    case 'rankedSearch/customFiltersChange':
      return { ...state, selectedFilters: action.selectedFilters };
    case 'rankedSearch/resetAll':
      return {
        ...state,
        rankedSearchResult: [],
        page: 0,
        hasError: false,
        totalHits: 0,
        hasMoreResults: false,
        isCprQuery: false,
        isUnauthorizedCprQuery: false,
        filterFields: [],
        selectedFilters: [],
      };
    default:
      return state;
  }
}

export function* executeQuery(resetSearchResult: boolean, createCitizenEnabled: boolean, searchClient: ISearchClient) {
  try {
    const state: IRankedSearchState = yield select(x => x.rankedSearch);
    yield put<UpdateIsLoading>({ type: 'rankedSearch/updateIsloading', isLoading: true });

    // Resets result to get the scrollbar to the top of the list.
    if (resetSearchResult) {
      yield put<ResetSearchResult>({ type: 'rankedSearch/resetSearchResult' });
    }

    const page = resetSearchResult ? 0 : state.page + 1;

    const result: GetRankedSearchResponse = yield searchClient.rankedSearchQuery({
      universe: state.universe,
      queryString: state.query,
      documentTypes: state.havings.filter(o => o.isChecked && o.universe === state.universe).map(o => o.value),
      page,
      selectedFilters: state.selectedFilters,
    });
    let { rankedSearchResult } = result;

    if (state.universe === 'company') {
      let companyByProductionUnitIdentifierResult: RankedSearchModel[] = [];
      if (rankedSearchResult?.length === 0 && isStringOnlyNumbers(state.query)) {
        const fasitSearchRes: GetCompanyByProductionUnitIdentifierResponse = yield searchClient.getCompanyByProductionUnitIdentifier({ productionUnitIdentifier: state.query });

        if (fasitSearchRes.company === null || fasitSearchRes.company?.length === 0) {
          const cvrSearchRes: GetCompanyFromCvrByProductionUnitIdentifierResponse = yield searchClient.getCompanyFromCvrByProductionUnitIdentifier({
            productionUnitIdentifier: state.query,
          });

          companyByProductionUnitIdentifierResult = cvrSearchRes.companies ?? [];
        } else {
          companyByProductionUnitIdentifierResult = fasitSearchRes.company ?? [];
        }
        // Always add this result, since we might want to hide it if our search isn't a p-nr. Because old successful searches should be hidden, if this current search has no hits.
        yield put<GetCompanyFromService>({
          type: 'rankedSearch/getCompanyFromService',
          result: companyByProductionUnitIdentifierResult,
          query: state.query,
        });
      } else {
        if (!resetSearchResult) {
          rankedSearchResult?.forEach(x => {
            state.rankedSearchResult?.push(x);
          });
          rankedSearchResult = [...(state.rankedSearchResult || [])];
        }
        yield put<ExecuteQuery>({
          type: 'rankedSearch/executeQuery',
          result: rankedSearchResult || [],
          totalHits: result.totalHits,
          hasMoreResults: result.hasMoreResults,
          page,
          filterFields: result.filterFields || [],
        });
      }
    } else if (state.universe === 'citizen') {
      if (createCitizenEnabled && rankedSearchResult?.length === 0 && isValidCpr(state.query)) {
        const indexedCitizenResult: GetRankedSearchResponse = yield searchClient.rankedSearchQuery({
          universe: 'citizen',
          queryString: state.query,
          documentTypes: ['citizen'],
          page: 0,
          selectedFilters: [],
        });
        const indexedCitizen = indexedCitizenResult.rankedSearchResult?.[0];

        if (indexedCitizen) {
          const missingDocumentTypes = state.havings
            .filter(having => having.isChecked && having.universe === 'citizen' && having.value !== 'citizen')
            .map(having => having.label.toLowerCase());

          if (missingDocumentTypes.length > 0) {
            indexedCitizen.description = `Borgeren har ingen ${missingDocumentTypes.join(', ')}`;
          }

          yield put<ExecuteQuery>({
            type: 'rankedSearch/executeQuery',
            result: [indexedCitizen],
            totalHits: 1,
            hasMoreResults: false,
            page: 0,
            filterFields: result.filterFields || [],
          });
        } else {
          const res: GetCitizenByCprResponse = yield searchClient.getCitizenByCpr({ citizenCpr: formatCprNoDash(state.query) });

          yield put<GetCitizenFromCim>({
            type: 'rankedSearch/getCitizenFromCim',
            result: res.citizen || [],
            isAuthorized: res.isAuthorized,
          });
        }
      } else {
        if (!resetSearchResult) {
          rankedSearchResult?.forEach(x => {
            state.rankedSearchResult?.push(x);
          });
          rankedSearchResult = [...(state.rankedSearchResult || [])];
        }

        yield put<ExecuteQuery>({
          type: 'rankedSearch/executeQuery',
          result: rankedSearchResult || [],
          totalHits: result.totalHits,
          hasMoreResults: result.hasMoreResults,
          page,
          filterFields: result.filterFields || [],
        });
      }
    } else {
      if (!resetSearchResult) {
        rankedSearchResult?.forEach(x => {
          state.rankedSearchResult?.push(x);
        });
        rankedSearchResult = [...(state.rankedSearchResult || [])];
      }

      yield put<ExecuteQuery>({
        type: 'rankedSearch/executeQuery',
        result: rankedSearchResult || [],
        totalHits: result.totalHits,
        hasMoreResults: result.hasMoreResults,
        page,
        filterFields: result.filterFields || [],
      });
    }
  } catch (error: any) {
    if (isAuthError(error)) {
      const { message, correlationIdentifier } = deconstructError(error);
      yield put<NotifyErrorAction>({ type: 'notify/enqueueError', message, correlationIdentifier });
    }
    yield put<UpdateHasError>({ type: 'rankedSearch/updateHasError', hasError: true });
  } finally {
    yield put<UpdateIsLoading>({ type: 'rankedSearch/updateIsloading', isLoading: false });
  }
}

export function* executeGetDocumentTypes(searchClient: ISearchClient) {
  try {
    const state: IRankedSearchState = yield select(x => x.rankedSearch);
    const result: RankedSearchDslConfigurationNameResponse = yield searchClient.rankedSearchDslConfigurationNamesQuery({ universe: state.universe });
    yield put<UpdateHaving>({
      type: 'rankedSearch/updateHaving',
      havings: result.rankedDocumentTypes?.map(x => ({ universe: x.universe, label: x.displayName, value: x.name, isChecked: x.enabledByDefault } as IHaving)) || [],
    });
  } catch (error: any) {
    yield put<UpdateHasError>({ type: 'rankedSearch/updateHasError', hasError: true });
  }
}

export function* getCitizenFromDfdg(cpr: string, searchClient: ISearchClient) {
  try {
    yield put<UpdateIsLoading>({ type: 'rankedSearch/updateIsloading', isLoading: true });

    // Resets result to get the scrollbar to the top of the list.
    yield put<ResetSearchResult>({ type: 'rankedSearch/resetSearchResult' });

    const result: GetCitizenFromDfdgResponse = yield searchClient.getCitizenFromDfdg({ citizenCpr: formatCprNoDash(cpr) });

    yield put<GetCitizenFromCim>({
      type: 'rankedSearch/getCitizenFromCim',
      result: result.citizen || [],
      isAuthorized: true,
    });
  } catch (error: any) {
    if (isAuthError(error)) {
      const { message, correlationIdentifier } = deconstructError(error);
      yield put<NotifyErrorAction>({ type: 'notify/enqueueError', message, correlationIdentifier });
    }
    yield put<ResetSearchResult>({ type: 'rankedSearch/resetSearchResult' });
  } finally {
    yield put<UpdateIsLoading>({ type: 'rankedSearch/updateIsloading', isLoading: false });
  }
}

export function* getCompanyFromCvr(productionUnitIdentifier: string, searchClient: ISearchClient) {
  try {
    yield put<UpdateIsLoading>({ type: 'rankedSearch/updateIsloading', isLoading: true });
    yield put<ResetSearchResult>({ type: 'rankedSearch/resetSearchResult' });

    const result: GetCompanyFromCvrByProductionUnitIdentifierResponse = yield searchClient.getCompanyFromCvrByProductionUnitIdentifier({ productionUnitIdentifier });

    yield put<GetCompanyFromService>({
      type: 'rankedSearch/getCompanyFromService',
      result: result.companies ?? [],
      query: productionUnitIdentifier,
    });
  } catch (error: any) {
    if (isAuthError(error)) {
      const { message, correlationIdentifier } = deconstructError(error);
      yield put<NotifyErrorAction>({ type: 'notify/enqueueError', message, correlationIdentifier });
    }
    yield put<ResetSearchResult>({ type: 'rankedSearch/resetSearchResult' });
  } finally {
    yield put<UpdateIsLoading>({ type: 'rankedSearch/updateIsloading', isLoading: false });
  }
}
function* customFiltersChange(selectedFilters: ISelectedFilter[]) {
  yield put<CustomFiltersChange>({ type: 'rankedSearch/customFiltersChange', selectedFilters });
  yield executeQuery(true, true, moduleClientFactory.createSearchClient());
}

function* watchCustomFiltersChange() {
  yield takeEvery<CustomFiltersChangeSaga>('rankedSearch/customFiltersChangeSaga', action => customFiltersChange(action.selectedFilters));
}

function* watchExecuteQuery() {
  yield takeEvery<ExecuteQuerySaga>('rankedSearch/executeQuerySaga', action =>
    executeQuery(action.resetSearchResult, action.createCitizenEnabled, moduleClientFactory.createSearchClient())
  );
}

function* watchExecuteGetDocumentTypesSaga() {
  yield takeEvery<ExecuteGetDocumentTypesSaga>('rankedSearch/executeGetDocumentTypesSaga', () => executeGetDocumentTypes(moduleClientFactory.createSearchClient()));
}

function* watchGetCitizenFromDfdgSaga() {
  yield takeEvery<GetCitizenFromDfdgSaga>('rankedSearch/getCitizenFromDfdgSaga', action => getCitizenFromDfdg(action.cpr, moduleClientFactory.createSearchClient()));
}

function* watchGetCompanyFromCvr() {
  yield takeEvery<GetCompanyFromCvr>('rankedSearch/getCompanyFromCvr', action => getCompanyFromCvr(action.productionUnitIdentifier, moduleClientFactory.createSearchClient()));
}

export function* rankedSearchSaga() {
  yield all([fork(watchExecuteQuery), fork(watchExecuteGetDocumentTypesSaga), fork(watchGetCitizenFromDfdgSaga), fork(watchGetCompanyFromCvr), fork(watchCustomFiltersChange)]);
}
